import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { collection, onSnapshot, query, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import adminService, { AdminUserRecord } from '../services/adminService';

const IMPERSONATION_STORAGE_KEY = 'pickupninja_impersonating_city_id';

interface AdminContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  dashboardStats: any;
  refreshDashboardStats: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;

  /** The signed-in admin's own admin_users document — role, own cityId, etc. */
  adminRecord: AdminUserRecord | null;
  /** Convenience accessors over adminRecord, so pages don't all repeat the
   *  same `adminRecord?.role === 'super_admin'` check. These are the admin's
   *  TRUE underlying identity — unaffected by impersonation, so e.g. the
   *  Super Admin nav section stays visible while a super_admin is viewing a
   *  city, letting them get back out. */
  isSuperAdmin: boolean;
  isCityAdmin: boolean;

  /**
   * True when the current view should behave like a city_admin's — either
   * they really are one, or a super_admin is impersonating a city. "Take
   * control" (per the standing goal) means seeing what that city admin sees,
   * restrictions included, not just filtering the data underneath an
   * unrestricted super_admin screen. Pages that render a different UI by
   * role (the master catalog vs. the toggle-only city catalog, for example)
   * should switch on this, not on isCityAdmin alone.
   */
  isActingAsCityAdmin: boolean;

  /**
   * A super_admin viewing one city's data as if they were its city_admin.
   * Null means "not impersonating" — a super_admin's default view is global.
   * A city_admin can never set this; effectiveCityId is always their own
   * city regardless of what this holds.
   */
  impersonatingCityId: string | null;
  startImpersonating: (cityId: string) => void;
  stopImpersonating: () => void;

  /**
   * The city every city-scoped query in this session should actually filter
   * by: a city_admin's own city, or a super_admin's impersonation target.
   * Null means "no city filter" — a super_admin's normal global view.
   */
  effectiveCityId: string | null;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

/** Check whether a Firebase Auth user has a document in the admin_users collection. */
async function verifyAdminRole(uid: string): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, 'admin_users', uid));
    return snap.exists();
  } catch (error: any) {
    // permission-denied means the user is definitely not an admin.
    if (error?.code === 'permission-denied') return false;
    console.error('Error verifying admin role:', error);
    return false;
  }
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRecord, setAdminRecord] = useState<AdminUserRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonatingCityId, setImpersonatingCityIdState] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    } catch {
      return null; // private-browsing / storage blocked — impersonation just won't persist across reloads
    }
  });
  const [dashboardStats, setDashboardStats] = useState({
    totalNinjas: 0,
    pendingNinjas: 0,
    approvedNinjas: 0,
    totalTasks: 0,
    activeTasks: 0,
    completedTasks: 0,
    totalCustomers: 0,
    totalRevenue: 0,
  });

  const loadAdminRecord = useCallback(async () => {
    const record = await adminService.getCurrentAdminRecord();
    setAdminRecord(record);
    return record;
  }, []);

  // ─── Auth State Observer ────────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        const adminStatus = await verifyAdminRole(firebaseUser.uid);
        setUser(firebaseUser);
        setIsAdmin(adminStatus);
        if (adminStatus) {
          await loadAdminRecord();
          refreshDashboardStats();
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        setAdminRecord(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Real-time Listeners (admin-gated) ────────────────────────────────────
  useEffect(() => {
    // Only set up admin listeners when the user is a verified admin.
    if (!user || !isAdmin) return;

    const unsubscribers: (() => void)[] = [];

    const handlePermissionError = (error: any, collection: string) => {
      if (error?.code === 'permission-denied') {
        console.warn(`[AdminContext] Permission denied for '${collection}' — admin role may have been revoked.`);
      } else {
        console.error(`[AdminContext] Listener error on '${collection}':`, error);
      }
    };

    // ninjas
    const ninjasUnsub = onSnapshot(
      query(collection(db, 'ninjas')),
      () => refreshDashboardStats(),
      (err) => handlePermissionError(err, 'ninjas')
    );
    unsubscribers.push(ninjasUnsub);

    // tasks
    const tasksUnsub = onSnapshot(
      query(collection(db, 'tasks')),
      () => refreshDashboardStats(),
      (err) => handlePermissionError(err, 'tasks')
    );
    unsubscribers.push(tasksUnsub);

    // users
    const usersUnsub = onSnapshot(
      query(collection(db, 'users')),
      () => refreshDashboardStats(),
      (err) => handlePermissionError(err, 'users')
    );
    unsubscribers.push(usersUnsub);

    return () => unsubscribers.forEach((u) => u());
  }, [user, isAdmin]);

  // ─── Dashboard Stats ────────────────────────────────────────────────────────
  const refreshDashboardStats = useCallback(async () => {
    try {
      const stats = await adminService.getDashboardStats();
      setDashboardStats(stats);
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        console.warn('[AdminContext] Permission denied when fetching dashboard stats.');
      } else {
        console.error('Error refreshing dashboard stats:', error);
      }
    }
  }, []);

  // ─── Auth Actions ───────────────────────────────────────────────────────────
  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const adminStatus = await verifyAdminRole(userCredential.user.uid);

    if (!adminStatus) {
      // Sign out the non-admin Firebase user and surface a clear error.
      await signOut(auth);
      throw new Error('Access denied. This account does not have admin privileges.');
    }

    setUser(userCredential.user);
    setIsAdmin(true);
    await loadAdminRecord();
    await refreshDashboardStats();
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAdmin(false);
      setAdminRecord(null);
      // A super_admin's impersonation choice is a live "who am I acting as"
      // decision, not a preference — it must not survive into the next
      // person to sign in on this browser.
      try {
        sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      } catch {
        // ignore — storage may be unavailable
      }
      setImpersonatingCityIdState(null);
      setDashboardStats({
        totalNinjas: 0,
        pendingNinjas: 0,
        approvedNinjas: 0,
        totalTasks: 0,
        activeTasks: 0,
        completedTasks: 0,
        totalCustomers: 0,
        totalRevenue: 0,
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to send password reset email');
    }
  };

  // ─── Impersonation ("view as city") ────────────────────────────────────────
  const startImpersonating = useCallback(
    (cityId: string) => {
      if (adminRecord?.role !== 'super_admin') return; // a city_admin has nothing to impersonate into
      try {
        sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, cityId);
      } catch {
        // ignore — falls back to in-memory only for this render
      }
      setImpersonatingCityIdState(cityId);
      adminService.logAdminActivity(`Started viewing city ${cityId} as its admin`, undefined, undefined, {
        actingAsCityId: cityId,
        isImpersonating: true,
      });
    },
    [adminRecord]
  );

  const stopImpersonating = useCallback(() => {
    const previous = impersonatingCityId;
    try {
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    } catch {
      // ignore
    }
    setImpersonatingCityIdState(null);
    if (previous) {
      adminService.logAdminActivity(`Stopped viewing city ${previous} as its admin`, undefined, undefined, {
        actingAsCityId: previous,
        isImpersonating: true,
      });
    }
  }, [impersonatingCityId]);

  const isSuperAdmin = adminRecord?.role === 'super_admin';
  const isCityAdmin = adminRecord?.role === 'city_admin';
  const isActingAsCityAdmin = isCityAdmin || (isSuperAdmin && !!impersonatingCityId);
  const effectiveCityId = isCityAdmin ? adminRecord?.cityId ?? null : isSuperAdmin ? impersonatingCityId : null;

  const value: AdminContextType = {
    user,
    isAdmin,
    loading,
    dashboardStats,
    refreshDashboardStats,
    login,
    logout,
    forgotPassword,
    adminRecord,
    isSuperAdmin,
    isCityAdmin,
    isActingAsCityAdmin,
    impersonatingCityId,
    startImpersonating,
    stopImpersonating,
    effectiveCityId,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
